import { useState, useEffect, useContext } from "react";
import { AuthContext } from "../../auth/AuthHandler";
import {
  ReleaseTrackerDataService,
  softwareIdToName,
} from "../../data/ReleaseTrackerDataService";
import { Switcher, DataTable, TopButtons } from "./../../components/index";
import { useBreadcrumb } from "../../hooks/useBreadcrumb";
import { checkUrlName } from "../../helper/checkName";

export const switcher = [
  ["Implementing Features"],
  ["Known Bugs"],
  ["Fixed Bugs"],
];
export const tableHeadFeatures = ["Reference", "Name", "Description"];
export const tableHeadKnownBugs = [
  "Reference",
  "Name",
  "Description",
  "Conditions",
  "Workaround",
  "Fixed In",
];
export const tableHeadFixedBugs = [
  "Reference",
  "Name",
  "Description",
  "Conditions",
  "Workaround",
];

export default function FeatureBugs({ match }) {
  const authCtx = useContext(AuthContext);
  const { locks_or_peripherals, url, name, version } = match.params;

  const [data, setData] = useState(null);
  const [versions, setVersions] = useState(null);
  const [selected, setSelected] = useState(false);
  const [selectedItem, setSelectedItem] = useState(switcher[0]);
  const [selectedVersion, setSelectedVersion] = useState(false);
  const [selectedVersionItem, setSelectedVersionItem] = useState("");

  // Update breadcrumbs when component mounts or params change
  useBreadcrumb(
    locks_or_peripherals
      ? [
          { label: "Home", path: "/" },
          { label: "Firmware", path: "/firmware" },
          {
            label: checkUrlName(locks_or_peripherals),
            path: `/firmware/${locks_or_peripherals}`,
          },
          {
            label: url,
            path: `/firmware/${locks_or_peripherals}/${url}`,
          },
          {
            label: name,
            path: `/firmware/${locks_or_peripherals}/${url}`,
          },
          { label: version },
        ]
      : [
          { label: "Home", path: "/" },
          { label: "Software", path: "/software" },
          { label: checkUrlName(url), path: `/software/${url}` },
          { label: name, path: `/software/${url}?tab=${name}` },
          { label: version },
        ],
    [locks_or_peripherals, url, name, version],
  );

  // Initial load and when params change
  useEffect(() => {
    const relTrackerService = new ReleaseTrackerDataService(authCtx);
    const params = new URLSearchParams(window.location.search);
    const tab = params.get("tab");
    const itemFromUrl = tab ? switcher.find((i) => i[0] === tab) : null;

    // Update selected version for ux purposes
    setSelectedVersion(true);
    setSelectedVersionItem("V" + version);

    // Fetch Data - check if this is firmware or software
    if (locks_or_peripherals) {
      // this is firmware
      relTrackerService
        .getFirmwareFamiliesFirmwareVersion(
          locks_or_peripherals,
          url,
          name,
          version,
        )
        .then((fetchedData) => {
          setData(fetchedData);
          setSelected(true);
          setSelectedItem(itemFromUrl ?? switcher[0]);
        })
        .catch((err) => console.error(err));

      // Fetch versions
      relTrackerService
        .getFirmwareFamiliesFirmwareVersions(locks_or_peripherals, url, name)
        .then((fetchedVersions) => setVersions(fetchedVersions))
        .catch((err) => console.error(err));
    } else {
      relTrackerService
        .getSoftwareVersion(softwareIdToName(url), name, version)
        .then((fetchedData) => {
          setData(fetchedData);
          setSelected(true);
          setSelectedItem(itemFromUrl ?? switcher[0]);
        })
        .catch((err) => console.error(err));

      // Fetch versions
      relTrackerService
        .getSoftwareVersions(softwareIdToName(url), name)
        .then((fetchedVersions) => setVersions(fetchedVersions))
        .catch((err) => console.error(err));
    }
  }, [locks_or_peripherals, url, name, version, authCtx]);

  const getHead = (tableHead) => {
    if (tableHead === switcher[0]) {
      return tableHeadFeatures;
    } else if (tableHead === switcher[1]) {
      return tableHeadKnownBugs;
    } else {
      return tableHeadFixedBugs;
    }
  };

  const setSelectedTab = (value) => {
    setSelected(true);
    setSelectedItem(value);
  };

  const showSwitcherTable = (switcherArray, dataObj) => {
    return (
      <>
        <div className="switcher">
          <div className="switcher-scroll">
            {switcherArray.map((value) => (
              <Switcher
                key={value}
                content={value}
                value={value}
                selected={selected}
                selectedItem={selectedItem}
                itemsLength={dataObj[value.toString().replace(/ /g, "")].length}
                onClick={() => setSelectedTab(value)}
              />
            ))}
          </div>
        </div>
        <DataTable
          key={selectedItem}
          tableHead={getHead(selectedItem)}
          data={dataObj}
          selectedItem={selectedItem}
        />
      </>
    );
  };

  if (!data) {
    return <div></div>;
  }

  return (
    <div className="feature-bugs">
      <div className="feature-bugs-header">
        <TopButtons />
        <div className="feature-bugs-header-version">
          <h3>{"Version " + version}</h3>
          <span>(Production Date: {data?.ReleaseDate})</span>
        </div>
        <p className="fwName">
          {softwareIdToName(url)} {name}
        </p>
      </div>
      <div className="versions">
        <div className="switcher">
          <div className="switcher-scroll">
            {versions &&
              versions.map((key) => (
                <Switcher
                  key={key.Code}
                  type="link"
                  url={{
                    pathname: locks_or_peripherals
                      ? `/firmware/${locks_or_peripherals}/${url}/${name}/${key.Code}`
                      : `/software/${url}/${name}/${key.Code}`,
                    data: key,
                  }}
                  content={"V" + key.Code}
                  selected={selectedVersion}
                  selectedItem={selectedVersionItem}
                />
              ))}
          </div>
        </div>
      </div>

      <div className="feature-bugs-content">
        {showSwitcherTable(switcher, data)}
      </div>
    </div>
  );
}
